import React, { useEffect, useMemo, useState } from 'react'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Textarea } from '../components/ui/textarea'
import { Label } from '../components/ui/label'
import { AtLimit, CharCounter } from '../components/ui/char-counter'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../components/ui/dialog'
import { Plus, Trash2, FolderKanban, Info, Pencil, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react'
import { workCategoryService } from '../services/workCategoryService'
import type { WorkCategory } from '../types'
import LoadingScreen from '../components/ui/LoadingScreen'
import { showSuccess, showError } from '../components/Alerts'
import { useAuth } from '../context/AuthContext'
import { PERMISSIONS } from '../constants/permissions'
import RequirePermission from '../components/RequirePermission'

const MAX_NAME = 50
const MAX_DESC = 200
const ITEMS_PER_PAGE = 10

const WorkItemConfig: React.FC = () => {
  const { hasPermission } = useAuth()
  const canEditConfig = hasPermission(PERMISSIONS.CONFIGURACION.EDITAR)

  const [rubros, setRubros] = useState<WorkCategory[]>([])
  const [asociatedRubrosIds, setAsociatedRubrosIds] = useState<number[]>([])
  const [isLoading, setIsLoading] = useState(false)

  // Nuevo rubro
  const [newName, setNewName] = useState('')
  const [newDesc, setNewDesc] = useState('')

  // Edición
  const [editOpen, setEditOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')
  const [editDesc, setEditDesc] = useState('')

  // Confirmación de eliminación
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [rubroToDelete, setRubroToDelete] = useState<WorkCategory | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Paginación
  const [page, setPage] = useState(1)

  const editingRubro = useMemo(
    () => rubros.find(r => r.id === editingId) ?? null,
    [rubros, editingId]
  )

  const loadRubros = async () => {
    try {
      setIsLoading(true)
      const response = await workCategoryService.getAllWorkCategories()
      setRubros(response)
    } catch (e) {
      console.error(e)
      showError('Error al cargar los rubros')
    } finally {
      setIsLoading(false)
    }
  }

  const loadAsociatedRubros = async () => {
    try {
      const ids: number[] = []
      setAsociatedRubrosIds(ids)
    } catch (e) {
      console.error('Error al cargar asociaciones:', e)
    }
  }

  useEffect(() => {
    ;(async () => {
      await Promise.all([loadRubros(), loadAsociatedRubros()])
    })()
  }, [])

  // Paginación calculada
  const totalPages = Math.ceil(rubros.length / ITEMS_PER_PAGE)
  const safePage = Math.min(page, Math.max(1, totalPages))
  const pageItems = rubros.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE)

  const handleAddRubro = async () => {
    if (!canEditConfig) return
    const name = newName.trim()
    const desc = newDesc.trim()
    if (!name) return

    try {
      setIsLoading(true)
      const created = await workCategoryService.createWorkCategory(name, desc || '')
      setRubros(prev => [...prev, created])
      setNewName('')
      setNewDesc('')
      showSuccess('Rubro creado exitosamente')
    } catch (e) {
      console.error(e)
      showError('Error al crear el rubro')
    } finally {
      setIsLoading(false)
    }
  }

  const openDeleteConfirm = (rubro: WorkCategory) => {
    if (!canEditConfig) return
    if (asociatedRubrosIds.includes(rubro.id)) return
    setRubroToDelete(rubro)
    setDeleteOpen(true)
  }

  const handleDeleteRubro = async () => {
    if (!rubroToDelete) return
    try {
      setIsDeleting(true)
      await workCategoryService.deleteWorkCategory(rubroToDelete.id)
      setRubros(prev => prev.filter(r => r.id !== rubroToDelete.id))
      setDeleteOpen(false)
      setRubroToDelete(null)
      showSuccess('Rubro eliminado exitosamente')
    } catch (e) {
      console.error(e)
      showError('Error al eliminar el rubro')
    } finally {
      setIsDeleting(false)
    }
  }

  const openEdit = (r: WorkCategory) => {
    if (!canEditConfig) return
    setEditingId(r.id)
    setEditName(r.value ?? '')
    setEditDesc(r.description ?? '')
    setEditOpen(true)
  }

  const handleSaveEdit = async () => {
    if (!canEditConfig || !editingId) return
    const value = editName.trim()
    const description = editDesc.trim()
    if (!value) return

    const prev = rubros
    setRubros(prev.map(r => r.id === editingId ? { ...r, value, description } : r))

    try {
      setIsLoading(true)
      await workCategoryService.updateWorkCategory(editingId, value, description)
      setEditOpen(false)
      setEditingId(null)
      showSuccess('Rubro actualizado exitosamente')
    } catch (e) {
      console.error(e)
      showError('Error al actualizar el rubro')
      setRubros(prev)
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading && rubros.length === 0) {
    return <LoadingScreen message="Cargando rubros" />
  }

  return (
    <>
      {/* Info Banner */}
      <div className="px-6 py-3.5 border-b border-border bg-blue-50/60 dark:bg-blue-900/20">
        <div className="flex items-start gap-2.5">
          <Info className="w-4 h-4 mt-0.5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
          <p className="text-sm text-blue-700 dark:text-blue-300">
            <strong>Información:</strong> Los rubros asociados a un tipo de obra no pueden ser
            eliminados. Para eliminar un rubro, primero desasocialo de todos los tipos de obra.
          </p>
        </div>
      </div>

      {/* Formulario de creación */}
      <RequirePermission permission={PERMISSIONS.CONFIGURACION.EDITAR}>
        <div className="px-6 py-5 border-b border-border">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground mb-4">
            <Plus className="w-4 h-4 text-primary" />
            Agregar nuevo rubro
          </h3>
          <div className="p-4 bg-muted/30 border border-border rounded-lg space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="new-rubro-name" className="text-xs font-medium text-foreground sm:text-sm">
                  Nombre <span className="text-xs text-red-500">(Obligatorio)</span>
                </Label>
                <Input
                  id="new-rubro-name"
                  placeholder="Ingrese nombre del rubro"
                  value={newName}
                  onChange={e => { if (e.target.value.length <= MAX_NAME) setNewName(e.target.value) }}
                  disabled={isLoading}
                  maxLength={MAX_NAME}
                  className="rounded-lg bg-background border-border focus-visible:ring-primary"
                />
                <AtLimit value={newName} max={MAX_NAME} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-rubro-desc" className="text-xs font-medium text-foreground sm:text-sm">
                  Descripción <span className="text-xs text-muted-foreground">(opcional)</span>
                </Label>
                <Textarea
                  id="new-rubro-desc"
                  placeholder="Descripción del rubro"
                  value={newDesc}
                  onChange={e => { if (e.target.value.length <= MAX_DESC) setNewDesc(e.target.value) }}
                  disabled={isLoading}
                  maxLength={MAX_DESC}
                  size="sm"
                  className="resize-none rounded-lg"
                />
                <CharCounter value={newDesc} max={MAX_DESC} />
              </div>
            </div>
            <Button
              onClick={handleAddRubro}
              disabled={isLoading || !newName.trim()}
              size="sm"
              className="h-9 px-5 rounded-lg"
              type="button"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              Agregar Rubro
            </Button>
          </div>
        </div>
      </RequirePermission>

      {/* Lista de rubros */}
      <div className="px-6 py-5">
        {rubros.length === 0 ? (
          <div className="py-14 text-center">
            <FolderKanban className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" />
            <p className="text-sm font-medium text-foreground mb-1">No hay rubros configurados</p>
            <p className="text-xs text-muted-foreground">Agregá el primero usando el formulario de arriba</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
              {pageItems.map(rubro => {
                const disabledDelete = asociatedRubrosIds.includes(rubro.id)
                return (
                  <div key={rubro.id} className="flex items-center gap-2">
                    <RequirePermission
                      permission={PERMISSIONS.CONFIGURACION.EDITAR}
                      fallback={
                        <div className="flex-1 min-w-0 px-4 py-3 border border-border rounded-lg bg-card">
                          <h4 className="text-sm font-medium truncate text-foreground">{rubro.value}</h4>
                          {rubro.description
                            ? <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{rubro.description}</p>
                            : <p className="text-xs italic text-muted-foreground mt-0.5">Sin descripción</p>
                          }
                        </div>
                      }
                    >
                      <>
                        <button
                          type="button"
                          onClick={() => openEdit(rubro)}
                          className="flex-1 min-w-0 px-4 py-3 text-left transition-colors border border-border rounded-lg bg-card hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                              <h4 className="text-sm font-medium truncate text-foreground">{rubro.value}</h4>
                              {rubro.description
                                ? <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{rubro.description}</p>
                                : <p className="text-xs italic text-muted-foreground mt-0.5">Sin descripción</p>
                              }
                            </div>
                            <Pencil className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                          </div>
                        </button>
                        <Button
                          onClick={() => openDeleteConfirm(rubro)}
                          disabled={isLoading || disabledDelete}
                          variant="outline"
                          size="sm"
                          className={[
                            'h-9 w-9 flex-shrink-0 rounded-lg p-0',
                            disabledDelete ? 'cursor-not-allowed opacity-40' : 'hover:border-destructive hover:bg-destructive/10'
                          ].join(' ')}
                          title={disabledDelete ? 'No se puede eliminar: rubro asociado' : 'Eliminar rubro'}
                          type="button"
                        >
                          <Trash2 className={disabledDelete ? 'h-3.5 w-3.5 text-muted-foreground' : 'h-3.5 w-3.5 text-destructive'} />
                        </Button>
                      </>
                    </RequirePermission>
                  </div>
                )
              })}
            </div>

            {/* Paginación */}
            <div className="flex items-center justify-between pt-4 mt-2 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Mostrando{' '}
                <span className="font-semibold text-foreground">{pageItems.length}</span> de{' '}
                <span className="font-semibold text-foreground">{rubros.length}</span>{' '}
                rubro{rubros.length !== 1 ? 's' : ''}
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={safePage === 1} className="rounded-lg">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-foreground tabular-nums">{safePage} / {totalPages}</span>
                  <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={safePage === totalPages} className="rounded-lg">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Dialog de edición */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-base font-semibold text-foreground">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                <Pencil className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              </div>
              Editar rubro
            </DialogTitle>
            {editingRubro && (
              <DialogDescription className="text-muted-foreground">
                Modificando: <span className="font-medium text-foreground">{editingRubro.value}</span>
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="edit-rubro-name" className="text-xs font-medium text-foreground sm:text-sm">
                Nombre <span className="text-xs text-red-500">(Obligatorio)</span>
              </Label>
              <Input
                id="edit-rubro-name"
                value={editName}
                onChange={e => { if (e.target.value.length <= MAX_NAME) setEditName(e.target.value) }}
                maxLength={MAX_NAME}
                placeholder="Nombre del rubro"
                autoFocus
                className="rounded-lg bg-background border-border focus-visible:ring-primary"
              />
              <AtLimit value={editName} max={MAX_NAME} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-rubro-desc" className="text-xs font-medium text-foreground sm:text-sm">Descripción</Label>
              <Textarea
                id="edit-rubro-desc"
                value={editDesc}
                onChange={e => { if (e.target.value.length <= MAX_DESC) setEditDesc(e.target.value) }}
                maxLength={MAX_DESC}
                placeholder="Descripción del rubro (opcional)"
                size="sm"
                className="resize-none rounded-lg"
              />
              <CharCounter value={editDesc} max={MAX_DESC} />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-2">
            <Button variant="outline" onClick={() => setEditOpen(false)} type="button" className="rounded-lg">
              Cancelar
            </Button>
            <RequirePermission permission={PERMISSIONS.CONFIGURACION.EDITAR}>
              <Button
                onClick={handleSaveEdit}
                disabled={isLoading || !editName.trim()}
                type="button"
                className="rounded-lg"
              >
                Guardar cambios
              </Button>
            </RequirePermission>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmación de eliminación */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground">
              <AlertCircle className="w-5 h-5 text-destructive" />
              Confirmar eliminación
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              ¿Estás seguro de que deseas eliminar el rubro{' '}
              <span className="font-medium text-foreground">"{rubroToDelete?.value}"</span>?
              Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={isDeleting} className="rounded-lg">
              Cancelar
            </Button>
            <RequirePermission permission={PERMISSIONS.CONFIGURACION.EDITAR}>
              <Button
                variant="destructive"
                onClick={handleDeleteRubro}
                disabled={isDeleting}
                className="rounded-lg"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {isDeleting ? 'Eliminando...' : 'Eliminar'}
              </Button>
            </RequirePermission>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default WorkItemConfig
